import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  IconButton,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Stack,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigation } from "contexts/notificationsContext";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";

import { TabFicha } from "./components/TabFicha";
import { TabTamanhosBarras } from "./components/TabTamanhosBarras";
import { TabDadosAdicionais } from "./components/TabDadosAdicionais";
import { TabComposicao } from "./components/TabComposicao";
import { ModalGerenciamentoMidias } from "./components/ModalGerenciamentoMidias";
import { ModalConfirmacaoExcluir } from "./components/ModalConfirmacaoExcluir";
import { ModalHistorico } from "./components/ModalHistorico";

import {
  IPerfil,
  ITamanhoBarra,
  IPerfilComponente,
  ISelectOption,
  TipoPerfil,
  PerfilTabIndex,
  SITUACAO_OPTIONS,
  STATUS_OPTIONS,
} from "./types/perfil.types";

import {
  obterPerfilPorId,
  salvarPerfil,
  excluirPerfil,
  duplicarPerfil,
  listarReferencias,
  listarLinhas,
  listarLigas,
  listarTemperas,
  listarPrenativosPorLinha,
  listarClasseIds,
  listarUnidadesValor,
  salvarComposicao,
  salvarTamanhosBarra,
  obterComposicao,
  validarCodigoUnico,
} from "./services/perfilService";
import { useValidacaoCodigoUnico } from "../hooks/useValidacaoCodigoUnico";
import { useSomenteLeituraN1 } from "../hooks/useSomenteLeituraN1";
import { AvisoPlanilhaPendenteN1 } from "../components/AvisoPlanilhaPendenteN1";
import { useFormularioAlterado } from "../hooks/useFormularioAlterado";

// FASE 05 — S3/PO 2026-05-13: IdTipoTela do Perfil. Valores do enum TipoTela
// (backend_novo/.../Empresa.Domain/Enums/TipoTela.cs).
const ID_TIPO_TELA_PERFIL = 1;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`perfil-tabpanel-${index}`}
      aria-labelledby={`perfil-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export const CadastroPerfil: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { openNotification } = useNavigation();

  // FASE 05 — 2ª validação/PO 2026-07-24 (B4): empresa "apenas consulta" = somente-leitura.
  // FASE 05 — 6ª validação/PO 2026-08-11: bloqueia edição também quando o MODO EDIÇÃO está desligado.
  // Plano 14/10/2026 (Frente 4): planilha de perfis em andamento também deixa a tela somente-leitura.
  const somenteLeitura = useSomenteLeituraN1("perfis");
  
  const isEdicao = !!id && id !== "novo";
  const idPerfil = isEdicao ? parseInt(id) : undefined;

  // Estados principais
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // FASE 05 — Ajustes 25/08 (item 4): SALVAR só habilita depois de alguma alteração.
  // Em cadastro novo não há estado inicial a comparar — o hook devolve sempre habilitado.
  const [tabIndex, setTabIndex] = useState<PerfilTabIndex>(0);
  
  // Dados do perfil
  const [perfil, setPerfil] = useState<Partial<IPerfil>>({
    codigo: "",
    descricao: "",
    idSituacao: 1, // Em edição
    idStatus: 1, // Ativo
    idUnidadeValor: 2, // Metro
    tipoPerfil: "cor_unica", // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 6): default "Cor Única" (reverte a P1 de 24/07)
    considerarPerda: true, // FASE 05 — 2ª validação/PO 2026-07-24 (F10): default "Sim"
    tamanhoBarras: [],
    composicao: [],
  });

  // Tamanhos de barras e composição separados para melhor controle
  const [tamanhoBarras, setTamanhoBarras] = useState<ITamanhoBarra[]>([]);
  const [composicao, setComposicao] = useState<IPerfilComponente[]>([]);

  // Listas de referência
  const [referencias, setReferencias] = useState<ISelectOption[]>([]);
  const [linhas, setLinhas] = useState<ISelectOption[]>([]);
  const [ligas, setLigas] = useState<ISelectOption[]>([]);
  const [temperas, setTemperas] = useState<ISelectOption[]>([]);
  const [prenativos, setPrenativos] = useState<ISelectOption[]>([]);
  const [classeIds, setClasseIds] = useState<ISelectOption[]>([]);
  const [unidadesValor, setUnidadesValor] = useState<ISelectOption[]>([]);

  // Modais
  const [modalMidiasAberto, setModalMidiasAberto] = useState(false);
  const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);

  // FASE 05 — S3/PO 2026-05-13: carrega apenas as listas globais ao montar
  // (Referencias filtradas pela empresa + listas independentes).
  // Linhas e Prenativos sao carregadas via useEffect quando Referencia/Linha mudam.
  const carregarListasReferencia = useCallback(async () => {
    try {
      const [refsData, ligasData, temperasData, classeIdsData, unidadesData] = await Promise.all([
        listarReferencias(ID_TIPO_TELA_PERFIL),
        listarLigas(),
        listarTemperas(),
        listarClasseIds(),
        listarUnidadesValor(),
      ]);

      setReferencias(refsData);
      setLigas(ligasData);
      setTemperas(temperasData);
      setClasseIds(classeIdsData);
      setUnidadesValor(unidadesData);
    } catch (error) {
      console.error("Erro ao carregar listas de referência:", error);
    }
  }, []);

  // Carregar dados do perfil (se edição)
  const carregarPerfil = useCallback(async () => {
    if (!idPerfil) {
      setLoading(false);
      return;
    }

    try {
      // Verificar se há composição no state do navigate (após salvar novo cadastro)
      const composicaoDoState = (location.state as any)?.composicao;
      
      const [perfilData, composicaoData] = await Promise.all([
        obterPerfilPorId(idPerfil),
        obterComposicao(idPerfil)
      ]);
      
      if (perfilData) {
        setPerfil(perfilData);
        setTamanhoBarras(perfilData.tamanhoBarras || []);
        // Usar composição do endpoint específico se disponível, senão usar do perfil ou do state
        const composicaoFinal = composicaoData || perfilData.composicao || composicaoDoState || [];
        setComposicao(composicaoFinal);
        
        // Limpar o state após usar
        if (composicaoDoState) {
          window.history.replaceState({}, document.title);
        }
      } else {
        openNotification({ message: "Perfil não encontrado", type: "error" });
        navigate("/produtosN1");
      }
    } catch (error) {
      console.error("Erro ao carregar perfil:", error);
      openNotification({ message: "Erro ao carregar perfil", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [idPerfil, navigate, openNotification, location.state]);

  useEffect(() => {
    carregarListasReferencia();
    carregarPerfil();
  }, [carregarListasReferencia, carregarPerfil]);

  // FASE 05 — S3+/PO 2026-05-13: validacao em tempo real de codigo duplicado.
  const { codigoDuplicado, validando: validandoCodigo } = useValidacaoCodigoUnico(
    perfil.codigo,
    idPerfil,
    validarCodigoUnico
  );

  // FASE 05 — S3/PO 2026-05-13: cascata Referencia -> Linha.
  // Recarrega o combo Linha sempre que a Referencia selecionada muda.
  useEffect(() => {
    if (perfil.idReferencia) {
      listarLinhas(perfil.idReferencia).then(setLinhas);
    } else {
      setLinhas([]);
    }
  }, [perfil.idReferencia]);

  // FASE 05 — S3/PO 2026-05-13: cascata Linha -> Prenativo.
  // Recarrega o combo Prenativo sempre que a Linha selecionada muda.
  useEffect(() => {
    if (perfil.idLinha) {
      listarPrenativosPorLinha(perfil.idLinha).then(setPrenativos);
    } else {
      setPrenativos([]);
    }
  }, [perfil.idLinha]);

  // Handlers
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue as PerfilTabIndex);
  };

  // FASE 05 — S3/PO 2026-05-13: ao trocar Referencia, limpar Linha e Prenativo.
  // Ao trocar Linha, limpar Prenativo. Isso evita estado inconsistente quando
  // o usuario muda um campo "pai" da cascata.
  const handlePerfilChange = (campo: keyof IPerfil, valor: any) => {
    setPerfil((prev) => {
      const next: Partial<IPerfil> = { ...prev, [campo]: valor };
      if (campo === "idReferencia") {
        next.idLinha = undefined;
        next.idPrenativo = undefined;
      } else if (campo === "idLinha") {
        next.idPrenativo = undefined;
      }
      return next;
    });
  };

  // Recarrega o cadastro após salvar, e o hook rearma o retrato sozinho — daí só `alterado`.
  // Plano 21/09/2026 (Frente 1): Tamanho de Barras e Composição são estados separados de
  // `perfil` — fora do retrato, mexer só nessas abas deixava SALVAR desabilitado e a alteração
  // se perdia (reporte de 21/09). Mesmo padrão do Componente ({ componente, cores }).
  const { alterado } = useFormularioAlterado(
    { perfil, tamanhoBarras, composicao },
    { ehNovo: !isEdicao, pronto: !loading }
  );

  const handleSalvar = async () => {
    // Validações
    if (!perfil.codigo?.trim()) {
      openNotification({ message: "Código é obrigatório", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!perfil.descricao?.trim()) {
      openNotification({ message: "Descrição é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }

    // FASE 05 — S3+/PO 2026-05-13: bloquear save se codigo ja cadastrado.
    if (codigoDuplicado) {
      // FASE 05 — 4ª validação/Frente 9 2026-07-30: mensagem oficial do PO (D6),
      // padronizada com o cadastro de Componente.
      openNotification({
        message: "Este código de produto já existe. Informe um código diferente ou inclua o sufixo da sua empresa ou entre em contato com o departamento de Produto Informação da Alumisoft.",
        type: "warning",
      });
      setTabIndex(0);
      return;
    }

    // FASE 05 — 2ª validação/PO 2026-07-24 (F11): exigir ao menos um Tamanho de Barra.
    if (!tamanhoBarras.some((b) => b.comprimento > 0)) {
      openNotification({ message: "Informe ao menos um Tamanho de Barra.", type: "warning" });
      setTabIndex(1); // aba TAMANHOS DE BARRAS
      return;
    }

    setSaving(true);
    try {
      const dadosParaSalvar: Partial<IPerfil> = {
        ...perfil,
        tamanhoBarras,
        // Não enviar composicao no salvarPerfil, será salva separadamente
      };

      const resultado = await salvarPerfil(dadosParaSalvar);
      if (resultado?.sucesso) {
        // Salvar composição separadamente se o perfil for composto
        if (perfil.tipoPerfil === "composto" && resultado.id && composicao.length > 0) {
          const composicaoSalva = await salvarComposicao(resultado.id, composicao);
          if (!composicaoSalva) {
            openNotification({ message: "Perfil salvo, mas houve erro ao salvar composição", type: "warning" });
          }
        }

        // FASE 05 — persistência 2026-08-10: salvar os tamanhos de barra em PerfisBarras.
        if (resultado.id) {
          await salvarTamanhosBarra(resultado.id, tamanhoBarras);
        }

        openNotification({ message: "Perfil salvo com sucesso", type: "success" });
        
        // Se era novo cadastro, redireciona para edição
        if (!isEdicao && resultado.id) {
          // Manter a composição no estado durante o redirecionamento
          navigate(`/produtosN1/perfis/${resultado.id}`, { 
            replace: true,
            state: { composicao: composicao } // Passar composição via state
          });
        } else if (resultado.id && String(resultado.id) !== String(idPerfil)) {
          // FASE 05 — 6ª validação/PO 2026-08-11 (F3 Etapa 2): o salvar congelou a versão de mercado
          // e redirecionou a edição para uma NOVA cópia de trabalho. Navegar para ela — senão a tela
          // recarregaria a versão de mercado congelada em vez das alterações.
          navigate(`/produtosN1/perfis/${resultado.id}`, {
            replace: true,
            state: { composicao: composicao },
          });
        } else {
          // Recarrega os dados
          await carregarPerfil();
          // Aguardar um pouco e recarregar a composição para garantir que está atualizada
          setTimeout(async () => {
            if (perfil.tipoPerfil === "composto" && resultado.id) {
              const composicaoRecarregada = await obterComposicao(resultado.id);
              if (composicaoRecarregada && composicaoRecarregada.length > 0) {
                setComposicao(composicaoRecarregada);
              }
            }
          }, 500);
        }
      }
    } catch (error: any) {
      // FASE 05 — 6ª validação/PO 2026-08-04: o catch só logava no console e a tela ficava muda
      // (mesmo bug do Superfície/Vidro). getErrorsByApi lança a mensagem real da API — exibi-la.
      console.error("Erro ao salvar:", error);
      openNotification({
        message: error?.message || "Erro ao salvar perfil. Tente novamente.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicar = async () => {
    if (!idPerfil) return;

    setSaving(true);
    try {
      const resultado = await duplicarPerfil(idPerfil);
      if (resultado?.sucesso && resultado.id) {
        openNotification({ message: "Perfil duplicado com sucesso", type: "success" });
        navigate(`/produtosN1/perfis/${resultado.id}`);
      }
    } catch (error) {
      console.error("Erro ao duplicar:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async () => {
    if (!idPerfil) return;

    const sucesso = await excluirPerfil(idPerfil);
    if (sucesso) {
      openNotification({ message: "Perfil excluído com sucesso", type: "success" });
      navigate("/produtosN1");
    }
    setModalExcluirAberto(false);
  };

  const handleVoltar = () => {
    navigate("/produtosN1");
  };

  // Verificar se aba COMPOSIÇÃO deve ser visível
  const mostrarAbaComposicao = perfil.tipoPerfil === "composto";

  // Formatação da data de última atualização
  const formatarDataAtualizacao = (data?: string) => {
    if (!data) return null;
    const d = new Date(data);
    return `${d.toLocaleDateString("pt-BR")} - ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
      <Grid container display={"flex"} alignItems={"center"}>
        {/* Header: Título + Breadcrumb - Fora do StackCustom para ter cor de fundo da página */}
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5} sx={{ width: "100%", px: 4.5 }}>
          <IconButton onClick={handleVoltar} size="small" sx={{ color: "#666" }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ color: "#333", fontWeight: 400, fontSize: "1.8rem" }}>
            Cadastro de Perfil
          </Typography>
        </Stack>
        
        {/* Breadcrumb abaixo do título */}
        <Box sx={{ 
          borderBottom: "2px solid #3C90AD", 
          pb: 1.5, 
          mb: 2,
          ml: 4.5,
          width: "100%",
        }}>
          <Breadcrumbs separator="›" sx={{ mt: 0 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Home
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/produtosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Cadastros
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/produtosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Perfis
            </Link>
            <Typography color="text.primary" sx={{ fontSize: "0.75rem" }}>
              {isEdicao ? "Editar" : "Novo Perfil"}
            </Typography>
          </Breadcrumbs>
        </Box>

        <StackCustom>
        {/* Última atualização e Log */}
        {isEdicao && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 2 }}>
            {perfil.dataUltimaAtualizacao && (
              <Typography variant="body2" color="text.secondary">
                Última atualização: {formatarDataAtualizacao(perfil.dataUltimaAtualizacao)}
              </Typography>
            )}
            <Link
              component="button"
              variant="body2"
              onClick={() => setModalHistoricoAberto(true)}
              sx={{ color: "#3C90AD" }}
            >
              Log de atualizações
            </Link>
          </Box>
        )}

        <AvisoPlanilhaPendenteN1 tipoMaterial="perfis" />

        {/* Abas */}
        <Box sx={{ mb: 0 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="Abas do cadastro de perfil"
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              minHeight: "auto",
              "& .MuiTabs-flexContainer": {
                gap: "2px",
              },
              "& .MuiTab-root": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                minHeight: 52,
                padding: "14px 28px",
                color: "#fff",
                backgroundColor: "#7ab8cc",
                borderRadius: 0,
                "&.Mui-selected": {
                  color: "#fff",
                  backgroundColor: "#3C90AD",
                },
                "&:hover": {
                  backgroundColor: "#5aa8c0",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "#3C90AD",
                },
              },
            }}
          >
            <Tab label="GERAL" />
            <Tab label="TAMANHOS DE BARRAS" />
            <Tab label="DADOS ADICIONAIS" />
            {mostrarAbaComposicao && <Tab label="COMPOSIÇÃO" />}
          </Tabs>
        </Box>

        {/* Conteúdo das Abas — bloqueado quando somente-leitura (B4). */}
        <Box sx={{ ...(somenteLeitura && { opacity: 0.7, pointerEvents: "none" }) }}>
        <TabPanel value={tabIndex} index={0}>
          <TabFicha
            perfil={perfil}
            onChange={handlePerfilChange}
            referencias={referencias}
            linhas={linhas}
            ligas={ligas}
            temperas={temperas}
            prenativos={prenativos}
            classeIds={classeIds}
            unidadesValor={unidadesValor}
            onOpenMidias={() => setModalMidiasAberto(true)}
            codigoDuplicado={codigoDuplicado}
            validandoCodigo={validandoCodigo}
          />
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          <TabTamanhosBarras
            tamanhoBarras={tamanhoBarras}
            onChange={setTamanhoBarras}
            figuraUrl={perfil.figuraUrl}
            onOpenMidias={() => setModalMidiasAberto(true)}
          />
        </TabPanel>

        <TabPanel value={tabIndex} index={2}>
          <TabDadosAdicionais
            perfil={perfil}
            onChange={handlePerfilChange}
            figuraUrl={perfil.figuraUrl}
            onOpenMidias={() => setModalMidiasAberto(true)}
          />
        </TabPanel>

        {mostrarAbaComposicao && (
          <TabPanel value={tabIndex} index={3}>
            <TabComposicao
              composicao={composicao}
              onChange={setComposicao}
              figuraUrl={perfil.figuraUrl}
              onOpenMidias={() => setModalMidiasAberto(true)}
            />
          </TabPanel>
        )}
        </Box>
        </StackCustom>

        {/* Rodapé com Botões de Ação - Fora do container principal.
            FASE 05 — 2ª validação/PO 2026-07-24 (B4): oculto em somente-leitura. */}
        {!somenteLeitura && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            mt: 3,
            width: "100%",
          }}
        >
          {isEdicao && (
            <>
              <Button
                variant="text"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setModalExcluirAberto(true)}
                disabled={saving}
              >
                EXCLUIR
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleDuplicar}
                disabled={saving}
                sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}
              >
                DUPLICAR
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSalvar}
            disabled={saving || !alterado}
            sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
          >
            SALVAR
          </Button>
        </Box>
        )}
      </Grid>

      {/* Modais */}
      <ModalGerenciamentoMidias
        open={modalMidiasAberto}
        onClose={() => setModalMidiasAberto(false)}
        perfil={perfil}
        onSave={(midias, idAlvo) => {
          setPerfil((prev) => ({ ...prev, ...midias }));
          setModalMidiasAberto(false);
          // Plano 21/09/2026 (Frente 3): perfil Disponível → a mídia foi para a cópia de
          // trabalho; navegar para ela (mesmo tratamento do handleSalvar quando o espelho cria a cópia).
          if (idAlvo && String(idAlvo) !== String(idPerfil)) {
            navigate(`/produtosN1/perfis/${idAlvo}`, { replace: true, state: { composicao } });
          }
        }}
      />

      <ModalConfirmacaoExcluir
        open={modalExcluirAberto}
        onClose={() => setModalExcluirAberto(false)}
        onConfirm={handleExcluir}
        codigo={perfil.codigo || ""}
        descricao={perfil.descricao || ""}
      />

      {idPerfil && (
        <ModalHistorico
          open={modalHistoricoAberto}
          onClose={() => setModalHistoricoAberto(false)}
          perfilId={idPerfil}
          perfilCodigo={perfil.codigo || ""}
        />
      )}
    </DashboardStackCustom>
  );
};

export default CadastroPerfil;
